import { supabase, unwrap, apiPost } from '../supabase';
import type { Webhook, WebhookDelivery } from '../types';

export const WEBHOOK_EVENTS = [
  {
    id: 'call_logged',
    label: 'Call logged',
    description: 'A call syncs from an employee’s phone.',
  },
  {
    id: 'recording_updated',
    label: 'Recording uploaded',
    description: 'A call recording finishes uploading.',
  },
  {
    id: 'ai_analysis',
    label: 'AI analysis ready',
    description: 'A transcript and summary are ready.',
  },
  {
    id: 'caller_updated',
    label: 'Contact updated',
    description: 'A contact’s name, note or labels change.',
  },
] as const;

const LEGACY_EVENTS: Record<string, string> = {
  'call.received': 'call_logged',
  recording_uploaded: 'recording_updated',
  'recording.available': 'recording_updated',
  'ai_analysis.completed': 'ai_analysis',
  'caller.updated': 'caller_updated',
  detail_updated: 'caller_updated',
};

export const canonicalEvent = (event: string) => LEGACY_EVENTS[event] ?? event;
export const eventLabel = (event: string) =>
  WEBHOOK_EVENTS.find((e) => e.id === canonicalEvent(event))?.label ?? event;

function mapWebhook(row: Record<string, unknown>): Webhook {
  const events = Array.isArray(row.event_types) ? (row.event_types as string[]) : [];
  return {
    id: String(row.id),
    name: String(row.name || 'Untitled endpoint'),
    url: String(row.url || ''),
    secret: String(row.secret || ''),
    eventTypes: [...new Set(events.map(canonicalEvent))],
    isActive: row.is_active !== false,
    includeFailures: Boolean(row.include_failures),
    createdAt: (row.created_at as string) || null,
  };
}

export async function listWebhooks(orgId: string): Promise<Webhook[]> {
  const rows = unwrap(
    await supabase
      .from('user_webhooks')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false }),
  ) as Record<string, unknown>[];
  return (rows ?? []).map(mapWebhook);
}

export function generateSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return `whsec_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export interface WebhookInput {
  name: string;
  url: string;
  eventTypes: string[];
  includeFailures: boolean;
}

export async function createWebhook(orgId: string, input: WebhookInput): Promise<void> {
  unwrap(
    await supabase.from('user_webhooks').insert({
      org_id: orgId,
      name: input.name.trim(),
      url: input.url.trim(),
      event_types: input.eventTypes,
      include_failures: input.includeFailures,
      is_active: true,
      secret: generateSecret(),
      updated_at: new Date().toISOString(),
    }),
  );
}

export async function updateWebhook(
  orgId: string,
  id: string,
  patch: Partial<WebhookInput> & { isActive?: boolean },
): Promise<void> {
  const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) update.name = patch.name.trim();
  if (patch.url !== undefined) update.url = patch.url.trim();
  if (patch.eventTypes !== undefined) update.event_types = patch.eventTypes;
  if (patch.includeFailures !== undefined) update.include_failures = patch.includeFailures;
  if (patch.isActive !== undefined) update.is_active = patch.isActive;
  unwrap(await supabase.from('user_webhooks').update(update).eq('id', id).eq('org_id', orgId));
}

export async function deleteWebhook(orgId: string, id: string): Promise<void> {
  unwrap(await supabase.from('user_webhooks').delete().eq('id', id).eq('org_id', orgId));
}

function mapDelivery(row: Record<string, unknown>): WebhookDelivery {
  const payload = (row.payload ?? {}) as Record<string, unknown>;
  const data = (payload.data ?? {}) as Record<string, unknown>;
  const code =
    row.response_status === null || row.response_status === undefined ? null : Number(row.response_status);
  const status = row.status
    ? String(row.status)
    : code === null
      ? 'pending'
      : code >= 200 && code < 300
        ? 'completed'
        : 'failed';
  const callId =
    row.queue_call_id ?? row.payload_data_call_id ?? data.call_id ?? row.payload_call_id ?? payload.call_id;
  return {
    id: row.id as string | number,
    eventType: String(row.event_type || row.payload_event || payload.event || ''),
    status,
    callId: callId === undefined || callId === null ? null : String(callId),
    responseStatus: code,
    createdAt: (row.created_at as string) || (row.attempted_at as string) || null,
  };
}

export async function listDeliveries(orgId: string): Promise<WebhookDelivery[]> {
  const queue = await supabase
    .from('webhook_queue')
    .select(
      'id,event_type,status,created_at,queue_call_id:call_id,payload_data_call_id:payload->data->>call_id,payload_call_id:payload->>call_id,payload_event:payload->>event',
    )
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (!queue.error && queue.data?.length) return (queue.data as Record<string, unknown>[]).map(mapDelivery);

  const logs = await supabase
    .from('webhook_logs')
    .select(
      'id,event_type,response_status,attempted_at,payload_data_call_id:payload->data->>call_id,payload_call_id:payload->>call_id,payload_event:payload->>event',
    )
    .eq('org_id', orgId)
    .order('attempted_at', { ascending: false })
    .limit(50);
  if (!logs.error) return ((logs.data ?? []) as Record<string, unknown>[]).map(mapDelivery);
  return [];
}

export interface TestResult {
  status: number;
  status_text: string;
  duration_ms: number;
  body: string;
}

export async function sendTestEvent(webhookId: string, event: string): Promise<TestResult> {
  return apiPost<TestResult>('/api/webhooks/test', { webhook_id: webhookId, event });
}

/** Mirrors the server's outbound URL guard so mistakes surface before saving. */
export function validateWebhookUrl(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return 'Enter a full URL, like https://example.com/webhooks/callsync';
  }
  if (url.protocol !== 'https:') return 'Use an https:// URL so call data is encrypted in transit.';
  const host = url.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.endsWith('.local') ||
    /^(10|127|0)\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
    /^169\.254\./.test(host)
  ) {
    return 'Private and local network addresses can’t receive webhooks.';
  }
  if (host.includes('webhook.site') && url.hash.includes('/view/')) {
    return 'That’s the webhook.site viewer link — copy “Your unique URL” instead.';
  }
  return null;
}
